// src/screens/FavoritesScreen.js
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { usersAPI, vehiclesAPI } from '../services/api';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import AppHeader from '../components/AppHeader';
import VehicleCard from '../components/VehicleCard';
import { useNavigate } from 'react-router-dom';

const FavoritesScreen = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [favorites, setFavorites] = useState([]); // array of vehicle objects, ordered
  const [loading, setLoading] = useState(true);
  const [savingOrder, setSavingOrder] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      setError('');
      const { data } = await usersAPI.getFavorites();
      setFavorites(data); // already populated, ordered
    } catch (err) {
      console.error('Fetch favorites error', err);
      const msg =
        err.response?.data?.message || err.message || 'Failed to load favorites.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchFavorites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Toggle from within this page (removes from list)
  const handleToggleFavorite = async (vehicleId) => {
    try {
      await vehiclesAPI.toggleFavorite(vehicleId);
      setFavorites((prev) => prev.filter((v) => v._id !== vehicleId));
    } catch (e) {
      console.error('Toggle in favorites error', e);
    }
  };

  // Reorder in memory
  const onDragEnd = async (result) => {
    const { destination, source } = result;
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) return;

    const newOrder = Array.from(favorites);
    const [moved] = newOrder.splice(source.index, 1);
    newOrder.splice(destination.index, 0, moved);
    setFavorites(newOrder);

    // Persist order
    try {
      setSavingOrder(true);
      const orderIds = newOrder.map((v) => v._id);
      await usersAPI.updateFavoritesOrder(orderIds);
    } catch (e) {
      console.error('Save order error', e);
      // Optional: revert if failed
      setError('Failed to save order');
      // await fetchFavorites();
    } finally {
      setSavingOrder(false);
    }
  };

  const empty = !loading && favorites.length === 0;

  return (
    <div className="flex flex-col h-full customer-friendly">
      <AppHeader title="Your Favorites" subtitle="Drag to rank your dream cars!" />

      <div className="flex-1 overflow-y-auto p-4 lg:p-6 lg:pb-6 pb-20 space-y-3 text-sm max-w-4xl lg:mx-auto w-full">
        {error && (
          <div className="bg-customer-accent-light text-customer-accent px-4 py-3 rounded-2xl text-sm font-medium">
            {error}
          </div>
        )}

        {savingOrder && (
          <div className="text-sm text-customer-teal font-medium">Saving your order...</div>
        )}

        {loading && (
          <div className="text-center py-8">
            <div className="inline-block w-8 h-8 border-4 border-customer-accent border-t-transparent rounded-full animate-spin mb-3"></div>
            <p className="text-sm text-app-text-secondary-light dark:text-app-text-secondary">Loading your favorites...</p>
          </div>
        )}

        {empty && (
          <div className="text-center text-app-text-secondary-light dark:text-app-text-secondary mt-10 bg-customer-accent-light/30 dark:bg-app-bg-secondary rounded-3xl p-8">
            <div className="text-4xl mb-3">❤️</div>
            <p className="text-lg font-medium text-app-text-light dark:text-app-text mb-2">No favorites yet!</p>
            <p className="text-sm">Tap the heart on any car that catches your eye to save it here.</p>
          </div>
        )}

        {!loading && favorites.length > 0 && (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="fav-list">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="space-y-2"
                >
                  {favorites.map((v, index) => (
                    <Draggable key={v._id} draggableId={v._id} index={index}>
                      {(provided2, snapshot) => (
                        <div
                          ref={provided2.innerRef}
                          {...provided2.draggableProps}
                          {...provided2.dragHandleProps}
                          className={`rounded-2xl transition-all ${
                            snapshot.isDragging ? 'ring-2 ring-customer-teal shadow-lg shadow-customer-teal/20' : ''
                          }`}
                        >
                          <VehicleCard
                            vehicle={v}
                            isFavorite={true}
                            onToggleFavorite={handleToggleFavorite}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
    </div>
  );
};

export default FavoritesScreen;
